import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { EditNetworkCorridorComposer } from "@/forms/sensnet/networkCorridors"
import { useApiQuery } from "@/api/ApiQueryServiceProvider"
import { useToast } from "@/contexts/ToastContext"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Network, Loader2 } from "lucide-react"

function EditNetworkCorridorPage() {
  const { networkCorridorNumber } = Route.useParams()
  const navigate = useNavigate()
  const { toast } = useToast()

  const { data, isLoading } = useApiQuery("getNetworkCorridorByNumber", { networkCorridorNumber })
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const c = (data as any)?.content?.networkCorridor

  const handleBack = () => navigate({ to: '/sensnet/network-corridors' })

  if (isLoading) {
    return (
      <div className="p-6 flex items-center justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  return (
    <div className="p-6 space-y-4">
      <Card className="shadow-sm pb-0 overflow-hidden">
        <CardHeader className="bg-slate-700 text-white [&_p]:text-slate-300 pb-6">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10">
              <Network className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle className="text-xl">Network Corridor</CardTitle>
              <CardDescription className="text-base">Update network corridor details</CardDescription>
            </div>
          </div>
        </CardHeader>
      </Card>
      <Card className="shadow-sm">
        <CardContent className="pt-6">
          {c ? (
            <EditNetworkCorridorComposer
              // Data-versioned key: re-mount when a fresh copy arrives so the form
              // re-initializes instead of showing previously-cached values.
              key={`corridor-${c.networkCorridorNumber}-${(c as any)?.updatedAt ?? ''}`}
              initialValues={{
                networkCorridorNumber: c.networkCorridorNumber,
                name: c.networkCorridorName || c.name || '',
                description: c.description || '',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                state: (c.state || undefined) as any,
                countiesCovered: c.countiesCovered || '',
                zipcodesCovered: c.zipcodesCovered || '',
                // Leave these EMPTY (undefined) when absent — do NOT seed 0. The schema
                // requires .positive() (> 0), so a seeded 0 makes the form invalid and
                // silently blocks the Save/Update button.
                numberOfPotentialSites: c.numberOfPotentialSites || undefined,
                expectedCorridorSize: c.expectedCorridorSize || undefined,
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                // Backend returns partner orgs as `partnerOrganizationNumbers` (renamed from
                // the old networkPartnerOrganizationIds). Read the current field first so
                // existing partners load into the multi-select; keep old names as fallbacks.
                networkPartner: ((c.partnerOrganizationNumbers || c.networkPartnerOrganizationIds || c.networkPartner || []) as any[]).map(Number),
                corridorPictures: [],
              }}
              submitLabel="Save Corridor"
              showHeader={false}
              onSuccess={handleBack}
              onError={(error) => toast({ title: 'Error', description: `Failed to update corridor: ${error.message}`, type: 'error' })}
              onCancel={handleBack}
            />
          ) : (
            <p className="text-sm text-muted-foreground">Network corridor not found.</p>
          )}
        </CardContent>
      </Card>
    </div>
  )
}

export const Route = createFileRoute("/sensnet/network-corridors/$networkCorridorNumber_/edit")({
  staticData: {
    title: 'Edit Network Corridor',
    isSecured: true,
  },
  component: EditNetworkCorridorPage,
})
